import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ShieldOff, TriangleAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { FLEET_ROUTES, type FleetDevice } from '../fleetApi';
import { apiMessage } from '../fleetHelpers';

/**
 * ## The kill switch, with the consequence spelled out before it is pulled
 *
 * Revoking a device is the one action on this screen that takes hardware out of
 * service in the middle of a shift, and the two situations it is used in could
 * not be further apart: a stolen tablet, and a spare going back in the drawer.
 * A one-line "are you sure?" serves neither — it is too weak for the first and
 * needlessly frightening for the second.
 *
 * So this dialog does three things a generic confirmation does not:
 *
 *  - **It lists what actually happens**, in the operator's terms rather than the
 *    API's: this device stops being able to sign in, whoever is signed in on it
 *    is signed out, and its tokens die immediately. Those are three separate
 *    effects of `DevicesService.revoke` and an operator can only weigh the
 *    decision if they know all three.
 *  - **It says what revoking does NOT do.** ADR-0009 draws the §6/§11 seam at
 *    authority: revoking a payment terminal takes it out of the *registry* and
 *    does not, and must not, attempt to reverse anything the acquirer already
 *    authorised. Somebody reaching for this button after a theft needs to know
 *    that before they reach for it, not afterwards.
 *  - **It asks for the device's name to be typed** before a live device can be
 *    killed. Not theatre: `isActive = false` is worn by a revoked device *and*
 *    by one that has simply never been paired, so the row six months from now
 *    cannot tell an operator which this was — the typed confirmation and the
 *    reason together are what make the record readable later. A device that has
 *    never been paired is not asked for it; there is nothing to take away.
 *
 * The reason is stored on the row (`revokedReason`), which is the whole point of
 * that column: "stolen from the counter on the 14th" and "replaced, in the
 * drawer" call for very different next steps.
 */
export default function RevokeDeviceDialog({
  device,
  onClose,
  onRevoked,
}: {
  device: FleetDevice | null;
  onClose: () => void;
  onRevoked: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [typedName, setTypedName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setReason('');
    setTypedName('');
  }, [device?.id]);

  if (!device) return null;

  // A device that never paired has no session to end and no token to kill, so
  // the name gate would be ceremony over nothing.
  const isLive = device.isActive && (device.pairing?.status ?? 'PAIRED') === 'PAIRED';
  const nameMatches = typedName.trim().toLowerCase() === device.name.trim().toLowerCase();
  const canSubmit = !busy && (!isLive || nameMatches);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(
        FLEET_ROUTES.deviceRevoke(device.id),
        reason.trim() ? { reason: reason.trim() } : {},
      );
      toast.success(t('fleet.devices.revoked', { name: device.name }));
      onRevoked();
      onClose();
    } catch (error) {
      toast.error(apiMessage(error, t('fleet.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('fleet.devices.revokeTitle', { name: device.name })}
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" disabled={!canSubmit} onClick={() => void submit()}>
            <Icon as={ShieldOff} />
            {t('fleet.devices.revoke')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-red-300 bg-red-50/70 p-3 dark:border-red-900 dark:bg-red-950/30">
          <p className="flex items-center gap-2 text-sm font-medium">
            <Icon as={TriangleAlert} className="text-red-600 dark:text-red-400" />
            {t('fleet.devices.revokeWhatHappens')}
          </p>
          <ul className="mt-2 list-disc space-y-1 ps-5 text-sm text-ink-muted">
            <li>{t('fleet.devices.revokeEffectLogin')}</li>
            <li>{t('fleet.devices.revokeEffectSession')}</li>
            <li>{t('fleet.devices.revokeEffectTokens')}</li>
          </ul>
        </div>

        <p className="text-sm text-ink-muted">{t('fleet.devices.revokeNotPayments')}</p>
        <p className="text-sm text-ink-muted">{t('fleet.devices.revokeReversible')}</p>

        <Field
          label={t('fleet.devices.revokeReason')}
          hint={t('fleet.devices.revokeReasonHint')}
        >
          <Input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('fleet.devices.revokeReasonPlaceholder')}
            maxLength={300}
          />
        </Field>

        {isLive && (
          <Field
            label={t('fleet.devices.revokeTypeName', { name: device.name })}
            hint={t('fleet.devices.revokeTypeNameHint')}
          >
            <Input
              value={typedName}
              onChange={(event) => setTypedName(event.target.value)}
              autoComplete="off"
            />
          </Field>
        )}
      </div>
    </Dialog>
  );
}
